import React, { useEffect, useState, useCallback } from "react";
import sigmetIcon from '../../images/sigmet.svg';
import { SIGMET_URL } from '../../data/config';

const formatQualifier = (qualifier) => {
  if (!qualifier) return null;
  
  const qualifierMap = {
    'OBSC': 'Obscured',
    'EMBD': 'Embedded',
    'FRQ': 'Frequent',
    'SEV': 'Severe',
    'ISOL': 'Isolated',
    'OCNL': 'Occasional',
    'HVY': 'Heavy',
    'SQL': 'Squall Line',
  };
  return qualifierMap[qualifier] || qualifier;
};

const formatHazard = (hazard) => {
  if (!hazard) return null;
  
  const hazardMap = {
    'TS': 'Thunderstorms',
    'TSGR': 'Thunderstorms with Hail',
    'TURB': 'Turbulence',
    'ICE': 'Icing',
    'MTW': 'Mountain Wave',
    'DS': 'Duststorm',
    'SS': 'Sandstorm',
    'FZRA': 'Freezing Rain',
    'VA': 'Volcanic Ash',
    'CLD': 'Cloud',
    'RDOACT CLD': 'Radioactive Cloud',
    'ICE (FZRA)': 'Icing (freezing rain)'
  };
  
  return hazardMap[hazard] || hazard;
};

const Sigmet = ({ map, openWindows, setOpenWindows, setWindowSize }) => {
  const [isVisible, setIsVisible] = useState(() => {
    return JSON.parse(localStorage.getItem("sigmetVisible")) || true;
  });
  const [data, setData] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);

  const addDataToMap = useCallback((data) => {
    if (!map || !data || !data.features) return;

    try {
      // Add or update the SIGMET data source
      if (!map.getSource("sigmet")) {
        map.addSource("sigmet", { type: "geojson", data });
      } else {
        map.getSource("sigmet").setData(data);
      }

      // Add SIGMET fill layer
      if (!map.getLayer("sigmet-layer")) {
        map.addLayer({
          id: "sigmet-layer",
          type: "fill",
          source: "sigmet",
          paint: {
            "fill-color": [
              "case",
              ["==", ["get", "hazard"], "ICE"], "rgba(100, 150, 255, 0.25)",
              ["in", "TS", ["get", "hazard"]], "rgba(255, 0, 0, 0.25)",
              "rgba(255, 100, 0, 0.25)"
            ],
            "fill-outline-color": [
              "case",
              ["==", ["get", "hazard"], "ICE"], "rgba(50, 100, 255, 1)",
              ["in", "TS", ["get", "hazard"]], "rgba(255, 0, 0, 1)",
              "rgba(255, 50, 0, 1)"
            ]
          }
        });
      }

      // Add SIGMET labels
      if (!map.getLayer("sigmet-labels")) {
        map.addLayer({
          id: "sigmet-labels",
          type: "symbol",
          source: "sigmet",
          layout: {
            "icon-image": [
              "case",
              ["in", "TS", ["get", "hazard"]], "ts",
              ["==", ["get", "hazard"], "MTW"], "mtw",
              ["==", ["get", "hazard"], "TURB"], "turb",
              ["in", "ICE", ["get", "hazard"]], "ice",
              ["in", "FZRA", ["get", "hazard"]], "fzra",
              ["==", ["get", "hazard"], "VA ERUPTION"], "volcano",
              ["==", ["get", "hazard"], "RADIOACT CLD"], "radioactive",
              "default-marker"
            ],
            "icon-size": 1,
            "icon-allow-overlap": true
          }
        });

        if (map.getLayer("traffic-layer")) {
          map.moveLayer("sigmet-layer", "lower-airspace");
          map.moveLayer("sigmet-labels", "traffic-layer");
        }

        // Add interactivity
        map.on("mouseenter", "sigmet-labels", () => {
          map.getCanvas().style.cursor = "pointer";
        });
      
        map.on("mouseleave", "sigmet-labels", () => {
          map.getCanvas().style.cursor = "";
        });

        map.on("click", "sigmet-labels", (e) => {
          const properties = e.features[0].properties;
          const firName = properties.firName || "Unknown FIR";
          const hazard = properties.hazard || "Unknown";
          const qualifier = properties.qualifier || "None";
          const verticalLimits = (properties.base !== undefined && properties.base !== null && 
            properties.top !== undefined && properties.top !== null)
            ? `ALT ${properties.base} — ${properties.top}`
            : "";

          const id = e.features[0].id || "//";

          const formatDate = (dateString) => {
            if (!dateString) return "Unknown";
            const date = new Date(dateString);
            return `${date.getUTCDate().toString().padStart(2, '0')}/${(date.getUTCMonth() + 1).toString().padStart(2, '0')}/${date.getUTCFullYear()} ${date.getUTCHours().toString().padStart(2, '0')}:${date.getUTCMinutes().toString().padStart(2, '0')}z`;
          };

          const content = (
            <div className="p-2 monospace-font text-sm overflow-y-auto mb-6" style={{ userSelect: 'text' }}>

              <div className="flex">
                <div className="pilotCallsign flex-1">{formatQualifier(qualifier)} {formatHazard(hazard)}</div>
                <div className="pilotCallsign flex-1">{verticalLimits || "No vert. limits"}</div>
              </div>

              <div className="flex items-center" style={{alignItems: "stretch"}}>
                <div className="aerodromedeparr flex-1 mr-1">
                  <span className="text-xs">Valid from</span><br/>
                  {formatDate(properties.validTimeFrom)}
                </div>
                <div className="aerodromedeparr flex-1 ml-1">
                  <span className="text-xs">Valid to</span><br/>
                  {formatDate(properties.validTimeTo)}
                </div>
              </div>
              <p className="pl-[2px] text-sm">RAW SIGMET</p>
              <div className="flightplan text-xs">
                {properties.rawSigmet || "No RAW SIGMET available"}
              </div>
            </div>
          );

          setOpenWindows((prev) => [
            ...prev.filter(win => win.title !== `SIGMET: ${firName}`),
            { id: `sigmet-window-${Date.now()}`, title: `SIGMET ${firName} #${id}`, content }
          ]);
          setWindowSize(2);
        });
      }
    } catch (error) {
      console.error("Error adding SIGMET layers:", error);
    }
  }, [map, setOpenWindows]);

  const updateVisibility = useCallback(() => {
    if (!map) return;

    const visibility = isVisible ? "visible" : "none";
    ["sigmet-layer", "sigmet-labels"].forEach(layer => {
      if (map.getLayer(layer)) {
        map.setLayoutProperty(layer, "visibility", visibility);
      }
    });
  }, [map, isVisible]);

  useEffect(() => {
    if (!map) return;

    const reloadData = () => {
      if (isVisible && data) {
        addDataToMap(data);
      }
    };

    map.on("style.load", reloadData);

    return () => {
      map.off("style.load", reloadData);
    };
  }, [map, isVisible, data, addDataToMap]);

  useEffect(() => {
    if (!map || !isVisible) return;
  
    const fetchData = async () => {
      try {
        const response = await fetch(SIGMET_URL);
        if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
        const data = await response.json();
        setData(data);
  
        if (!map.isStyleLoaded()) {
          map.once("styledata", () => addDataToMap(data));
        } else {
          addDataToMap(data);
        }
  
        setHasFetched(true);
      } catch (error) {
        console.error("Failed fetching SIGMETs:", error);
      }
    };
  
    // Always fetch if SIGMET was already enabled at startup
    if (!hasFetched) {
      fetchData();
    } else {
      addDataToMap(data);
    }
  }, [map, isVisible, hasFetched, addDataToMap]);  

  useEffect(() => {
    updateVisibility();
  }, [isVisible, updateVisibility]);

  useEffect(() => {
    return () => {
      if (map) {
        ["sigmet-layer", "sigmet-labels"].forEach(layer => {
          if (map.getLayer(layer)) map.removeLayer(layer);
        });
  
        if (map.getSource("sigmet")) map.removeSource("sigmet");
  
        // Remove event listeners
        map.off("mouseenter", "sigmet-labels");
        map.off("mouseleave", "sigmet-labels");
        map.off("click", "sigmet-labels");
      }
    };
  }, [map]);

  const toggleVisibility = () => {
    setIsVisible(prev => {
      const newVisibility = !prev;
      localStorage.setItem("sigmetVisible", JSON.stringify(newVisibility));
      if (newVisibility) setHasFetched(false); // Ensure re-fetching
      return newVisibility;
    });
  };

  return (
    <button
      data-tooltip-id="map-buttons-tooltip"
      data-tooltip-content="SIGMET"
      className={`w-8 h-8 ${isVisible ? "bg-gray-600" : "bg-gray-400"} text-white flex items-center justify-center`}
      onClick={toggleVisibility}
    >
      <img className="w-6" src={sigmetIcon} alt="Sigmets" />
    </button>
  );
};

export default Sigmet;
